// src/components/CheckoutForm/CheckoutForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import ResponsiveButton from "../../components/ResponsiveButton/ResponsiveButton";

export default function CheckoutForm({
  userId, nimi, sahkoposti, puhelinnumero,
  cart, selectedDates, onSuccess, onReservationSaved
}) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculateTotal = useCallback(() => {
    if (!Array.isArray(cart) || cart.length === 0 || !selectedDates?.startDate || !selectedDates?.endDate) {
      return 0;
    }
    const days = Math.ceil(
      (new Date(selectedDates.endDate) - new Date(selectedDates.startDate)) / (1000 * 60 * 60 * 24)
    ) || 1;

    return cart.reduce(
      (sum, item) => sum + (item.price || 0) * days * (item.quantity || 1),
      0
    );
  }, [cart, selectedDates]);

  useEffect(() => {
    const parsedUserId = parseInt(userId, 10);
    const validUserId = !isNaN(parsedUserId);

    // Guard: vain kun meillä on taulukko koreista, päivämäärät, kelvollinen userId, nimi, sähköposti ja clientSecret vielä puuttuu
    if (
      !Array.isArray(cart) ||
      cart.length === 0 ||
      !selectedDates?.startDate ||
      !validUserId ||
      !nimi?.trim() ||
      !sahkoposti?.trim() ||
      clientSecret
    ) {
      return;
    }

    const fetchPaymentIntent = async () => {
      try {
        const response = await fetch("https://spiderlegsproductions.com/api/create_payment_intent.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: parsedUserId,
            cart,
            start_date: selectedDates.startDate,
            end_date: selectedDates.endDate,
            customerName: nimi,
            customerEmail: sahkoposti,
            totalAmount: calculateTotal()
          })
        });

        const data = await response.json();
        if (data.clientSecret) {
          setClientSecret(data.clientSecret);
        } else {
          toast.error(data.message || "Maksuvalmistelu epäonnistui.");
          console.error("create_payment_intent ilman clientSecretia:", data);
        }
      } catch (error) {
        console.error("Stripe API-kutsu epäonnistui:", error);
        toast.error("Maksuvalmistelu epäonnistui.");
      }
    };

    fetchPaymentIntent();
  }, [
    userId,
    cart,
    selectedDates,
    nimi,
    sahkoposti,
    calculateTotal,
    clientSecret
  ]);

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      toast.error("Maksutoiminto ei ole vielä valmis.");
      return;
    }
    if (!clientSecret) {
      toast.error("Maksuvalmistelu kesken, odota hetki.");
      return;
    }
    if (!nimi?.trim() || !sahkoposti?.trim()) {
      toast.error("Nimi ja sähköposti vaaditaan maksua varten.");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      toast.error("Korttikenttää ei löytynyt.");
      return;
    }

    setLoading(true);
    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: { name: nimi, email: sahkoposti }
        }
      });

      if (error) {
        toast.error(`Maksu epäonnistui: ${error.message}`);
        setLoading(false);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        // Tallenna varaus backendille
        const resp2 = await fetch('https://spiderlegsproductions.com/api/save_reservation.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            nimi,
            sahkoposti,
            puhelinnumero,
            cart: cart.map(i => ({ id: i.id, quantity: i.quantity })),
            start_date: selectedDates.startDate,
            end_date: selectedDates.endDate
          })
        });
        const result2 = await resp2.json();
        if (result2.success) {
          toast.success('✅ Maksu ja varaus onnistui!');
          onReservationSaved(result2.varausID);
          onSuccess();
          // Haetaan profiilityyppi ja ohjataan
          const profileRes = await fetch("https://spiderlegsproductions.com/api/get_rental_user.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId })
          });
          const profileData = await profileRes.json();
          const profileType = profileData?.user?.profile_type;
          if (profileType === "performer") navigate("/performer-dashboard");
          else if (profileType === "rental") navigate("/rental-dashboard");
          else navigate("/");
        } else {
          toast.error('Varausta ei tallennettu: ' + result2.message);
        }
      } else {
        toast.error("Maksu ei onnistunut.");
      }
    } catch (err) {
      console.error("❌ Virhe maksuprosessissa:", err);
      toast.error("Maksun käsittelyssä tapahtui virhe.");
    } finally {
      setLoading(false);
    }
  };

  if (!stripe || !elements) {
    return <p className="font-customFont1">Ladataan maksutoiminto…</p>;
  }

  return (
    <div>
      {clientSecret ? (
        <>
          <label htmlFor="card-element" className="block font-customFont1 font-bold mb-1">
            Korttitiedot
          </label>
          <div id="card-element" className="p-4 bg-white rounded mb-4">
            <CardElement options={{
              style: {
                base: { fontSize: '16px', fontFamily: 'customFont1, sans-serif', color: '#111827', '::placeholder': { color: '#6B7280' } },
                invalid: { color: '#EF4444' }
              }
            }} />
          </div>
        </>
      ) : (
        <p className="font-customFont1">Valmistelen maksua…</p>
      )}

      <ResponsiveButton
        type="button"
        onClick={handleSubmit}
        disabled={!clientSecret || loading}
        className="px-4 py-2 font-customFont1 rounded"
      >
        {loading ? 'Käsittelen…' : `Maksa ${calculateTotal().toFixed(2)} € ja lähetä lomake`}
      </ResponsiveButton>
    </div>
  );
}

CheckoutForm.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  nimi: PropTypes.string.isRequired,
  sahkoposti: PropTypes.string.isRequired,
  puhelinnumero: PropTypes.string.isRequired,
  cart: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    quantity: PropTypes.number.isRequired,
    price: PropTypes.number
  })).isRequired,
  selectedDates: PropTypes.shape({
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired
  }).isRequired,
  onSuccess: PropTypes.func.isRequired,
  onReservationSaved: PropTypes.func.isRequired
};
